import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAbility } from '@casl/react';
import { MongoAbility } from '@casl/ability';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { axiosRequest } from '@lib/axios';
import { roleFromString, Roles } from '@lib/constants';
import {
  clearStoredTokens,
  getStoredTokens,
  hasStoredTokens,
  isAdmin as isAdminRaw,
  setStoredTokens,
} from '@lib/auth';
import { AbilityContext, defaultAbility } from '@lib/permissions';
import { jwtDecode } from 'jwt-decode';
import { unpackRules } from '@casl/ability/extra';
import { QueryCache } from '@tanstack/react-query';

export const UserContext = createContext({});

export const UserProvider = (props) => {
  // The decoded payloads of the accessToken and idToken.
  // (Raw tokens can be gotten by calling getStoredTokens())
  const [userSession, setUserSession] = useState(null);
  // The user record returned from the auth endpoints (represents the row in the users table)
  const [userRecord, setUserRecord] = useState(null);
  // The user's primary role, stored as a string. This is the highest role they have in myEMS or Cognito.
  // e.g., if they are both 'myEMS Staff' and 'myEMS Administrator', this will equal 'myEMS Administrator'.
  const [userRole, setUserRole] = useState(null);

  const [loadingRecord, setLoadingRecord] = useState(false);
  // We always attempt authentication on page load so this starts as true
  const [authenticating, setAuthenticating] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  /** @type {MongoAbility} ability **/
  const ability = useAbility(AbilityContext);

  const navigate = useNavigate();

  // Additional "authenticating" status reference to avoid issues in state updates
  const authRef = useRef(false);

  const [searchParams] = useSearchParams();

  const queryCache = new QueryCache();

  /** Stores tokens in persistent storage, and decodes them to store their decoded data in context state. */
  const updateAuthTokens = (tokens) => {
    const decodedTokens = {
      accessToken: tokens.accessToken ? jwtDecode(tokens.accessToken) : null,
      idToken: tokens.idToken ? jwtDecode(tokens.idToken) : null,
      // Note: We don't need the payload of the refresh token.
    };
    // console.log('[UserContext] Decoded auth tokens', decodedTokens);
    setUserSession(decodedTokens);
    // We also return the decoded tokens, because other code might use them that can't wait for state to update.
    // (e.g., login form)
    return decodedTokens;
  };

  /**
   * Stores user and permissions data in the context and sets up CASL permissions
   */
  const updateUserData = (user, role, rules) => {
    setUserRecord(user);
    setUserRole(typeof role === 'string' ? roleFromString(role) : role);
    ability.update(unpackRules(rules));
  };

  /**
   * Stores data for the logged in user in context.
   * @param response The data from the login endpoint.
   */
  const signIn = (response) => {
    const { auth: { user, role, permissions }, ...tokens } = response;
    if (!user) {
      setAuthenticating(false);
      return;
    }
    const tokensInput = {
      accessToken: tokens.AccessToken,
      idToken: tokens.IdToken,
      refreshToken: tokens.RefreshToken,
    };
    setStoredTokens(tokensInput);
    const decodedTokens = updateAuthTokens(tokensInput);
    updateUserData(user, role, permissions);

    setAuthenticating(false);
    setAuthenticated(true);
    return decodedTokens;
  };

  /**
   * Sign out the current user and redirect them to the home page.
   * @returns {Promise<void>}
   */
  const signOut = async () => {
    clearSession();
    location.href = `/`;
  };

  const clearSession = () => {
    queryCache.clear();
    clearStoredTokens();
    setUserSession(null);
    setUserRecord(null);
    setUserRole(null);
    ability.update(unpackRules([]));
    console.log('[UserContext] User signed out.');
  }

  const isSSO = () => userRecord?.is_sso;

  const fullName = useMemo(() => {
    if (!userRecord) {
      return '';
    }
    if (userRecord?.first_name && userRecord?.last_name) {
      return `${userRecord.first_name} ${userRecord.last_name}`;
    }
    // For some SSO users, mostly in prod, the firstName and lastName attributes don't get
    // set in Cognito, and thus are null in our DB. There is a 'name' attribute in the
    // IdToken that we can use as a fallback.
    if (userSession?.idToken?.name) {
      return userSession.idToken.name;
    }
    // And if that doesn't work, fall back to the email address.
    return userRecord?.email;
  }, [userRecord, userSession]);

  const hasRole = useCallback((roles) => {
    if (Array.isArray(roles)) {
      return roles.includes(userRole);
    }
    return roles === userRole;
  }, [userRole]);

  /**
   * User is "logged in" if they have a session and user record stored in memory OR session tokens in storage (meaning
   * they will be re-authenticated on the next request).
   * @returns {boolean}
   */
  const isLoggedIn = (justTokens = true) => {
    // console.log('[isLoggedIn] userState', userSession, !!userSession?.authToken, !!userRecord?.id);
    const hasSession = !!userSession?.accessToken && !!userRecord?.id;
    const hasTokens = hasStoredTokens();
    // console.log('[isLoggedIn] hasSession', hasSession, 'hasTokens', hasTokens);
    return (hasSession || (justTokens && hasTokens));
  };

  const isAdmin = () => {
    const groups = userSession?.idToken['cognito:groups'] || [];
    return isAdminRaw(groups);
  };

  /**
   * Load user record from the DB. Used when rehydrating a session, e.g., when user opens a new tab.
   */
  const loadUserRecord = useCallback(async () => {
    // console.log('[userContext] Loading user record...');
    setLoadingRecord(true);
    try {
      const response = await axiosRequest({ method: 'GET', url: '/auth/users/current' });
      const { auth: { user, role, permissions } } = response;
      let actualRole = role;
      // Admins can impersonate a staff user by adding ?masquerade=1 to the query string of any page.
      // This is helpful for tasks like recording training videos, so the admin-specific UI elements
      // don't appear.
      if (roleFromString(role) === Roles.SUPER_ADMIN) {
        actualRole = searchParams.get('masquerade') === '1' ? Roles.STAFF : Roles.SUPER_ADMIN;
      }
      if (user?.id) {
        updateUserData(user, actualRole, permissions);
      } else {
        console.error('[UserContext] No user record and/or user role retrieved.');
        await signOut();
      }
    } catch (err) {
      console.log('[UserContext] Error loading user:', err);
      await signOut();
      setLoadingRecord(false);
    }
    // eslint-disable-next-line
  }, [userSession, searchParams]);

  /**
   * Rehydrates the data in context when a user opens a new window or tab.
   */
  const pageLoadRestoreAuth = useCallback(async () => {
    if (authRef.current) {
      return;
    }
    if (!hasStoredTokens()) {
      setAuthenticating(false);
      setAuthenticated(false);
      return;
    }
    authRef.current = true;
    setAuthenticating(true);
    setAuthenticated(false);
    try {
      const tokens = getStoredTokens();
      updateAuthTokens(tokens);
      await loadUserRecord();
      setAuthenticated(true);
    } finally {
      authRef.current = false;
      setAuthenticating(false);
    }
    // eslint-disable-next-line
  }, [loadUserRecord, loadingRecord]);

  const handleAuthCode = async (code) => {
    authRef.current = true;
    setAuthenticating(true);
    try {
      // console.log('[UserContext] SSO login: Getting tokens from code', code);
      const response = await axiosRequest({
        method: 'GET',
        url: '/auth/sso-verify',
        params: {
          code
        },
        anonymous: true
      });
      // console.log('[UserContext] Got tokens from SSO code:', response);
      signIn(response);
    } catch (err) {
      console.log('login error', err);
      await signOut();
      throw err;
    }
    authRef.current = false;
    setAuthenticating(false);
  };

  // When opening a new window or tab, we try to rehydrate the user session based on tokens
  // in local storage.
  useEffect(() => {
    pageLoadRestoreAuth().then();
    // eslint-disable-next-line
  }, []);

  return (
    <UserContext.Provider value={{
      userSession,
      setUserSession,
      userRecord,
      setUserRecord,
      userRole,
      setUserRole,
      fullName,

      // For backwards compatibility.
      userState: {
        userSession,
        userRecord,
        userRole
      },

      updateAuthTokens,
      updateUserData,

      handleAuthCode,
      signIn,
      signOut,
      isAdmin,
      isLoggedIn,
      isSSO,
      hasRole,
      loadingRecord,
      authenticating,
      setAuthenticating,
      authenticated,
      clearSession,
    }}>
      <AbilityContext.Provider value={defaultAbility}>
        <>{props.children}</>
      </AbilityContext.Provider>
    </UserContext.Provider>
  );
};

export default UserContext;
